// ---------------------------------------------------------------------------
// How an aggregated round is read: what it says, and when each part of it is
// allowed on the screen.
//
// A `.ts` and not a `.tsx` for the same reason `ruling.ts` is one — three files
// beside it ask these questions and none of them may answer one for itself.
//
// --- How the aggregated reveal paces itself --------------------------------
//
// **It is a table plus a finale, and the table is the only thing that is
// paced.** An ordinary reveal is a queue of entries, one per seat, each holding
// the stage for `revealHoldMs`. That is exactly what does not survive contact
// with five hundred seats: the whole reason `aggregateReveal` exists is that a
// queue whose length is the size of the room is not a slow version of a reveal,
// it is a different one. So nothing here calls `revealHoldMs`, and the
// aggregated screen never has more than `AGGREGATE_ROWS` things to let out.
//
// What it does instead, and why each half is the way it is:
//
//   **The eight rows fill in worst-first, from the bottom up**, so the round's
//   best answer is the last thing to land and it lands on TOP. That is the
//   letters leaderboard's own shape (`LettersLeaderboard`), and it is the same
//   shape for the same reason: a panel that assembles itself into a leaderboard
//   in front of the room is the reveal, where a table drawn complete in one
//   frame is a result.
//
//   **The release signal is the server's cursor and nothing local.** CLAUDE.md's
//   rule that the server paces the reveal is not suspended by the room being
//   large: `revealCursor / revealOrder.length` is a monotonic 0..1 that every
//   client in the room reads off the same payload at the same moment, and
//   `aggregateRowsOut` is the only place it is turned into a number of rows. No
//   timer here, no local clock, nothing that could put two phones on different
//   rows.
//
//   **The best row is withheld until the whole queue is out, which is what buys
//   it its moment.** The engine's reveal runs one step past the end of its queue
//   — CLAUDE.md, and `revealStepCount` in the engine — and that trailing step
//   releases nobody: it is the last answer's own hold, `REVEAL_TAIL_BONUS_MS`
//   included. Releasing the top row at `cursor === revealOrder.length` puts it on
//   the screen at the exact instant that hold begins, so it gets the whole of it
//   before `revealComplete` moves the stage to the payoff. Nothing had to be
//   invented for that; the trailing step already existed and this reuses it. Do
//   not "simplify" the last row out to `revealComplete`, which is one step later
//   and is the same frame the payoff arrives in — that is precisely the bug the
//   trailing step was added to fix, reintroduced on a new screen.
//
//   **Then the finale, which is the ordinary payoff, untouched.** The round
//   seen whole (`distribution`), then the best answer the board allowed, then the
//   standing — each on its own `REVEAL_PAYOFF_BEAT_MS`. Isaac was explicit that
//   he likes the current ending and does not want it flattened into the first row
//   of a table, so `BestWordFrame` and `SolverFrame` are reached by exactly the
//   route they were reached by before. The table is what replaced the queue; it
//   did not replace the payoff.
//
// A conundrum is the exception in both modes and for the reason CLAUDE.md gives:
// it has no reveal steps at all, `revealStepCount` returns 0 for one, and its
// whole reveal is available the instant it is judged. So every row of an
// aggregated conundrum is out immediately and nothing here holds one back.
// ---------------------------------------------------------------------------

import { AGGREGATE_NAMES_SHOWN } from '@countdown/shared';
import type {
  AggregateEntry,
  ClientGameState,
  ClientRound,
  RoundAggregate,
} from '@countdown/shared';
import { listNames } from '../../../lib/format.js';
import { playerById } from '../../../lib/playerLookup.js';

/**
 * The round as a distribution, or null when this is an ordinary reveal.
 *
 * The field is optional as well as nullable — an older payload has neither — so
 * this is the one place the `?? null` is written and every caller asks it rather
 * than reaching for `round.aggregate` and getting `undefined` somewhere it
 * expected a null.
 */
export function aggregateOf(round: ClientRound): RoundAggregate | null {
  return round.aggregate ?? null;
}

/**
 * How many of `aggregate.top` the reveal has let out, counted from the WORST.
 *
 * See the header for why this is derived from the server's cursor rather than
 * from a timer, and why the top row lands on the trailing step rather than on
 * `revealComplete`.
 *
 * Three edge cases, each a real state rather than a defensive `?? 0`:
 *
 *   - a round with an empty `top` has nothing to release and returns 0;
 *   - a round with no queue at all — a conundrum, or a round nobody answered —
 *     is complete the moment it is judged, so the whole table is out;
 *   - a cursor of zero is the lead beat, where the stage is deliberately blank.
 */
export function aggregateRowsOut(round: ClientRound): number {
  const aggregate = aggregateOf(round);
  const total = aggregate?.top.length ?? 0;
  if (total === 0) return 0;
  if (round.revealComplete === true) return total;

  const queue = (round.revealOrder ?? []).length;
  if (queue === 0) return total;

  const cursor = Math.min(Math.max(round.revealCursor ?? 0, 0), queue);
  if (cursor === 0) return 0;
  // `ceil` rather than `round`: with more seats than rows the first release has
  // to put something on the screen, or the table sits empty through the part of
  // the reveal where the room is watching hardest.
  return Math.min(total, Math.max(1, Math.ceil((cursor / queue) * total)));
}

/**
 * The rows currently on the table, best first.
 *
 * `top` arrives best-first — it is ranked by SCORE, see `RoundAggregate.top` —
 * and the release order is the reverse of it, so the slice is taken off the END
 * and then drawn in the order it came. The first row of what this returns is
 * therefore always the newest as well as the best, which is what lets the panel
 * mark one row without remembering anything between renders.
 */
export function releasedAggregateRows(round: ClientRound): AggregateEntry[] {
  const aggregate = aggregateOf(round);
  if (aggregate === null) return [];
  const out = aggregateRowsOut(round);
  return out === 0 ? [] : aggregate.top.slice(aggregate.top.length - out);
}

/**
 * The ruled-out entries and the ones that stand, out of the rows that are out.
 *
 * **Every rejected answer collapses into one row and that is the whole point.**
 * Isaac: invalid words need to be lumped together somewhere, because showing
 * them one per row is what makes a big round unreadable. They are also the rows
 * released first — `top` is ranked by score and a rejected answer scores zero,
 * so the ruled-out ones sit at the bottom of the eight and come out at the front
 * of the queue — which means the lump forms early and the good answers stack
 * above it. That falls out of the ranking rather than being arranged here.
 *
 * What is NOT claimed: how many of `otherAnswers` were ruled out. The aggregate
 * does not carry that split and this must not invent it — see
 * `otherAnswersLine`, which says what it knows and stops.
 */
export function splitByRuling(rows: AggregateEntry[]): {
  stood: AggregateEntry[];
  ruledOut: AggregateEntry[];
} {
  return {
    stood: rows.filter((row) => row.valid),
    ruledOut: rows.filter((row) => !row.valid),
  };
}

/** How many players are behind a set of rows, named or not. */
export function playersBehind(rows: AggregateEntry[]): number {
  return rows.reduce((sum, row) => sum + row.count, 0);
}

/**
 * Who found an answer, in the words the row says out loud.
 *
 * `ALICE, BOB and 12 others` — the names are capped at `AGGREGATE_NAMES_SHOWN`
 * on the wire and `count` carries the truth, so the tail is arithmetic rather
 * than a list that was cut short here.
 *
 * A named id that no longer resolves is a seat that has left the room, and it is
 * dropped from the names while staying inside `count`: the row still says how
 * many found the word, it just cannot say what one of them was called. When
 * nothing resolves at all the row falls back to the bare count, which is the
 * honest thing a five-hundred-seat room's payload can say.
 */
export function findersLine(state: ClientGameState, entry: AggregateEntry): string {
  const named = entry.playerIds
    .slice(0, AGGREGATE_NAMES_SHOWN)
    .map((playerId) => playerById(state, playerId))
    .filter((player) => player !== null)
    .map((player) => player.displayName);

  const others = Math.max(0, entry.count - named.length);
  if (named.length === 0) return others === 1 ? '1 player' : `${others} players`;
  // `listNames` only where the list is the whole of it. With a tail on the end
  // its `and` would be the second one in the line — `Alice and Bob and 12
  // others` — and the first of the two would read as joining the last name to
  // the count rather than to the list.
  if (others === 0) return listNames(named);
  return `${named.join(', ')} and ${others === 1 ? '1 other' : `${others} others`}`;
}

/**
 * Everything the eight rows do not account for, in one line, or null when there
 * is nothing left over.
 *
 * **It is never dropped and it is never guessed at.** CLAUDE.md's instinct about
 * silent caps applies to a table that shows eight of forty answers: the other
 * thirty-two are on the histogram beside it and they are counted here, so the
 * eight read as the top of something rather than as the whole of it.
 */
export function otherAnswersLine(aggregate: RoundAggregate): string | null {
  const { otherAnswers, otherPlayers } = aggregate;
  if (otherAnswers === 0) return null;
  const answers = otherAnswers === 1 ? '1 other answer' : `${otherAnswers} other answers`;
  const players = otherPlayers === 1 ? '1 player' : `${otherPlayers} players`;
  return `${answers}, from ${players}`;
}

/** Seats that put up nothing at all, in one line, or null when everybody did. */
export function noAnswerLine(aggregate: RoundAggregate): string | null {
  if (aggregate.noAnswer === 0) return null;
  return aggregate.noAnswer === 1 ? '1 played nothing' : `${aggregate.noAnswer} played nothing`;
}

/**
 * Whether this round's conundrum was raced for or simply answered.
 *
 * `settings.conundrumBuzzer` off is the rule for a room too big to race — see
 * the setting's own note — and the reveal for it says how many got it rather
 * than who got there first. Asked here rather than at each of the four places
 * that word something differently, so the wording cannot drift.
 */
export function conundrumIsRaced(state: ClientGameState): boolean {
  return state.settings.conundrumBuzzer !== false;
}
